import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

/**
 * Theme Provider for managing light/dark mode
 * 
 * Supports three modes:
 * - 'dark': Force dark theme (default)
 * - 'system': Follow browser/OS preference
 * - 'light': Force light theme
 * 
 * Persistence:
 * - localStorage for immediate access (prevents flash)
 * - Firebase user settings for cross-device sync
 */
export function ThemeProvider({ children }) {
  // Get initial preference from localStorage, default to 'dark'
  const [themePreference, setThemePreferenceState] = useState(() => {
    if (typeof window === 'undefined') return 'dark';
    return localStorage.getItem('theme-preference') || 'dark';
  });
  
  // Track if preference was loaded from database
  const [isInitialized, setIsInitialized] = useState(false);
  
  // The actual theme being displayed ('light' or 'dark')
  const [resolvedTheme, setResolvedTheme] = useState(() => {
    if (typeof window === 'undefined') return 'dark';
    const currentTheme = document.documentElement.getAttribute('data-theme');
    return currentTheme || 'dark';
  });
  
  // Callback for when theme preference changes (for DB sync)
  const [onChangeCallback, setOnChangeCallback] = useState(null);
  
  // Initialize theme from user data (called when user profile loads)
  const initializeFromUserData = useCallback((userThemePreference) => {
    if (userThemePreference && ['system', 'light', 'dark'].includes(userThemePreference)) {
      setThemePreferenceState(userThemePreference);
      localStorage.setItem('theme-preference', userThemePreference);
      setIsInitialized(true);
    }
  }, []);
  
  // Set theme preference with optional callback for DB sync
  const setThemePreference = useCallback((newPreference) => {
    setThemePreferenceState(newPreference);
    localStorage.setItem('theme-preference', newPreference);
    
    // Trigger callback for database sync if registered
    if (onChangeCallback) {
      onChangeCallback(newPreference);
    }
  }, [onChangeCallback]);
  
  // Register a callback to be called when theme changes (for DB sync)
  const registerOnChange = useCallback((callback) => {
    setOnChangeCallback(() => callback);
  }, []);
  
  // Handle theme resolution and system preference changes.
  // The index.html inline script already sets data-theme before React mounts,
  // so we skip DOM writes when the resolved value hasn't actually changed.
  useEffect(() => {
    const resolveTheme = () => {
      if (themePreference === 'system') {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      }
      return themePreference;
    };

    const applyTheme = (theme) => {
      const current = document.documentElement.getAttribute('data-theme');
      if (current !== theme) {
        document.documentElement.setAttribute('data-theme', theme);
        const bgColor = theme === 'dark' ? '#1D1D1D' : '#FAFAFA';
        document.documentElement.style.backgroundColor = bgColor;
        const metaThemeColor = document.querySelector('meta[name="theme-color"]');
        if (metaThemeColor) {
          metaThemeColor.setAttribute('content', bgColor);
        }
      }
    };

    const newTheme = resolveTheme();
    setResolvedTheme(newTheme);
    applyTheme(newTheme);

    if (themePreference === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e) => {
        const systemTheme = e.matches ? 'dark' : 'light';
        setResolvedTheme(systemTheme);
        applyTheme(systemTheme);
      };
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [themePreference]);
  
  const value = {
    themePreference,
    setThemePreference,
    resolvedTheme,
    isDark: resolvedTheme === 'dark',
    // Methods for DB integration
    initializeFromUserData,
    registerOnChange,
    isInitialized,
  };
  
  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Hook to access theme context
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
